import * as React from "react";
import { Col, Row } from "react-bootstrap";
import { useQuery } from "@apollo/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { byPrefixAndName } from "@awesome.me/kit-d560caf08c/icons/modules";

import {
  GET_PAKIHI,
  Pakihi,
  PakihiQueryData,
  RarangiPakihiData,
  showLoad,
} from "@plinknz/tah-website-elements";

import { Hero } from "../hero";
import { Loader } from "../loader";
import { NotFound } from "../../views/404";
import { Dropdown } from "../form/dropdown";

export interface RarangiPakihiProps {
  data: RarangiPakihiData;
}

function renderer({
  name,
  region,
  details,
  benefits,
  website,
  address,
  email,
  phone,
  industry,
}: Pakihi) {
  let webLink = website;

  if (website && !website.match(/^http/)) {
    webLink = `https://${website}`;
  }

  return (
    <div
      data-testid="pakihi-grid"
      className="pakihi-grid content || constrain-width"
    >
      <div className="pakihi-title" data-testid="pakihi-title">
        {name}
      </div>
      <Row>
        <Col xs={12} sm={6} className="mb-0">
          {region && (
            <Row>
              <Col xs={4} className="pakihi-heading m-0">
                Region(s)
              </Col>
              <Col xs={8} className="m-0" data-testid="pakihi-region">
                {region}
              </Col>
            </Row>
          )}
          {industry && (
            <Row>
              <Col xs={4} className="pakihi-heading m-0">
                Industry
              </Col>
              <Col xs={8} className="m-0" data-testid="pakihi-industry">
                {industry}
              </Col>
            </Row>
          )}
          {benefits && (
            <Row>
              <Col xs={4} className="pakihi-heading m-0 d-none d-sm-block">
                Rangitāne Benefits
              </Col>
              <Col xs={4} className="pakihi-heading m-0 d-block d-sm-none">
                Benefits
              </Col>
              <Col xs={8} className="m-0" data-testid="pakihi-benefits">
                {benefits}
              </Col>
            </Row>
          )}
        </Col>
        <Col xs={12} sm={6} className="mt-xs-0">
          {phone && (
            <Row>
              <Col xs={2} className="m-0">
                <a target="_blank" href={`tel:${phone}`} rel="noreferrer">
                  <FontAwesomeIcon
                    icon={byPrefixAndName.fas["phone-office"]}
                    className="footer-icon-inflate"
                  />
                </a>
              </Col>
              <Col xs={10} className="m-0">
                <a
                  target="_blank"
                  href={`tel:${phone}`}
                  rel="noreferrer"
                  data-testid="pakihi-phone"
                >
                  {phone}
                </a>
              </Col>
            </Row>
          )}
          {email && (
            <Row>
              <Col xs={2} className="m-0">
                <a target="_blank" href={`mailto:${email}`} rel="noreferrer">
                  <FontAwesomeIcon
                    icon={byPrefixAndName.fas.envelope}
                    className="footer-icon-inflate"
                  />
                </a>
              </Col>
              <Col xs={10} className="m-0">
                <a
                  target="_blank"
                  href={`mailto:${email}`}
                  rel="noreferrer"
                  data-testid="pakihi-email"
                >
                  {email}
                </a>
              </Col>
            </Row>
          )}
          {website && (
            <Row>
              <Col xs={2} className="m-0">
                <a target="_blank" href={webLink} rel="noreferrer">
                  <FontAwesomeIcon
                    icon={byPrefixAndName.fas["globe-pointer"]}
                    className="footer-icon-inflate"
                  />
                </a>
              </Col>
              <Col xs={10} className="m-0">
                <a
                  target="_blank"
                  href={webLink}
                  rel="noreferrer"
                  data-testid="pakihi-website"
                >
                  {website}
                </a>
              </Col>
            </Row>
          )}
          {address && (
            <Row>
              <Col xs={2} className="m-0">
                <FontAwesomeIcon
                  icon={byPrefixAndName.fas["map-location-dot"]}
                  className="footer-icon-inflate"
                />
              </Col>
              <Col xs={10} className="m-0" data-testid="pakihi-address">
                {address}
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      <Row>
        <Col xs={12} data-testid="pakihi-details">
          {details}
        </Col>
      </Row>
    </div>
  );
}

function render({
  items,
  onChange,
  selected,
}: {
  items: Pakihi[];
  onChange: (val: string) => void;
  selected: string;
}) {
  const names = items.map(({ name }) => name);
  names.unshift("-- Select Pakihi --");

  return (
    <div data-testid="pakihi-page" className="content || constrain-width">
      <Dropdown
        label="Select a pakihi from the list below to view their details."
        options={names}
        onChange={onChange}
        selected={selected}
      />
    </div>
  );
}

export const RarangiPakihi = ({ data }: RarangiPakihiProps) => {
  const { pakihi } = data;
  const pakihiExists = !!pakihi.length;
  const [currentPakihi, setPakihi] = React.useState<Pakihi>();

  const { loading, data: queryData, error } = useQuery<PakihiQueryData>(
    GET_PAKIHI
  );

  function onChange(pakihiName: string) {
    const thisPakihi = pakihiExists
      ? pakihi.find(({ name }) => name === pakihiName)
      : queryData.pakihis.find(({ name }) => name === pakihiName);

    if (thisPakihi) {
      setPakihi(thisPakihi);
    }
  }

  if (pakihiExists) {
    return (
      <>
        {render({
          items: pakihi,
          onChange,
          selected: currentPakihi?.name,
        })}
        {currentPakihi && renderer(currentPakihi)}
      </>
    );
  }

  if (!queryData && showLoad(loading)) {
    return (
      <div className="splash">
        <Loader size="large" />
      </div>
    );
  }

  if (!showLoad(loading) && !error && queryData?.pakihis.length === 0) {
    return <NotFound />;
  }

  if (error) {
    console.warn(error);
    const errorMessage = error?.graphQLErrors
      .map((graphqlError) =>
        graphqlError.extensions?.exception
          ? // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-member-access
            `${graphqlError.extensions.exception.output.statusCode} ${graphqlError.extensions.exception.message}`
          : null
      )
      .filter(Boolean)
      .join(", ");

    return (
      <Hero title="There was an unexpected error" subtitle={errorMessage} />
    );
  }

  return (
    <>
      {render({
        items: queryData?.pakihis || [],
        onChange,
        selected: currentPakihi?.name,
      })}
      {currentPakihi && renderer(currentPakihi)}
    </>
  );
};
